import { Button, Icon, Text } from '@user-portal/wikr-components';
import { cancelFlow_subscriptionClick } from 'analytics/trackers/cancelFlow';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useTrackAmplitudeEvent } from 'services/Analytics/useAmplitude';

import { SUBSCRIPTION_LABEL_STATES } from '../constants';
import { IPausedSubscription } from '../types';

import AdditionalStatusInfo from './subComponents/AdditionalStatusInfo';
import BillingPeriod from './subComponents/BillingPeriod';
import CancelSubscriptionBlock from './subComponents/CancelSubscriptionBlock';
import { IncludedInYourPlanRow } from './subComponents/IncludedInYourPlanRow';
import PeriodRow from './subComponents/PeriodRow';
import PriceRow from './subComponents/PriceRow';
import SubscriptionCardHeader from './subComponents/SubscriptionCardHeader';
import SubscriptionStatusLabel from './subComponents/SubscriptionLabel';
import SubscriptionCardWrapper from './SubscriptionCardWrapper';

import * as S from './styled';

const PausedSubscription = ({
    tabIcon,
    heading,
    subscriptionPeriod,
    price,
    isTrial,
    isUpgrade,
    externalId,
    state,
    isReminderSet,
    isMainSubscription,
    productCode,
    captions,
    nextChargeDate,
    onResumeSubscription,
    onCancelSubscription,
    isTimePassedSincePaused,
    isNextChargeAvailable,
    isOneTimeProduct,
    isPromoCodeUsed,
}: IPausedSubscription) => {
    const { t } = useTranslation();
    const trackAmplitudeEvent = useTrackAmplitudeEvent();

    const handleResume = () => {
        onResumeSubscription();

        cancelFlow_subscriptionClick({
            status: state,
            action: 'resume',
            reminder: isReminderSet,
            free_month: false,
            subscription_id: externalId,
        });
        // trackAmplitudeEvent('account_subscription__click');
    };

    const handleCancel = () => {
        onCancelSubscription();

        cancelFlow_subscriptionClick({
            status: state,
            action: 'cancel',
            reminder: isReminderSet,
            free_month: false,
            subscription_id: externalId,
        });
        trackAmplitudeEvent('account_subscription__click');
    };

    return (
        <SubscriptionCardWrapper>
            <SubscriptionCardHeader
                isMainSubscription={isMainSubscription}
                productCode={productCode}
                Icon={tabIcon}
                heading={heading}
            />
            <S.StatusesWrapper>
                <SubscriptionStatusLabel state={SUBSCRIPTION_LABEL_STATES.PAUSED} />
                {!isTimePassedSincePaused && <SubscriptionStatusLabel state={SUBSCRIPTION_LABEL_STATES.UPDATING} />}
            </S.StatusesWrapper>
            <PeriodRow period={subscriptionPeriod} isTrial={isTrial} />
            <PriceRow price={price} />
            <BillingPeriod period={subscriptionPeriod} />
            {isUpgrade && <IncludedInYourPlanRow isCardRow />}
            {isNextChargeAvailable && (
                <S.SubscriptionCardRow>
                    <Text>{t('subscription.text.newChargeDate')}</Text>
                    <Text>{`${nextChargeDate} UTC`}</Text>
                </S.SubscriptionCardRow>
            )}
            <S.Line $mt={16} />
            {captions?.title && <AdditionalStatusInfo captions={captions} />}
            <S.Disclaimer>
                <Icon name="info" color="text-secondary-subdued" size={24} />
                <S.SubscriptionDisclaimer type="medium-text" color="text-secondary" mb={10}>
                    {t('subscription.management.description')}
                </S.SubscriptionDisclaimer>
            </S.Disclaimer>
            {!isOneTimeProduct && !isPromoCodeUsed && (
                <Button mb={20} dataLocator="resumeSubscriptionButton" onClick={handleResume}>
                    {t('subscription.text.resumeSub')}
                </Button>
            )}
            <CancelSubscriptionBlock
                isOneTimeProduct={isOneTimeProduct}
                date={nextChargeDate}
                onCancel={handleCancel}
            />
        </SubscriptionCardWrapper>
    );
};

export default PausedSubscription;
